import { type Room, RoomEvent, type RemoteParticipant, type LocalTrackPublication } from 'livekit-client';
import { DebugLog } from './DebugLog.js';

interface Mgr { pcManager?: { subscriber?: { getConnectedPC?: () => RTCPeerConnection | null; pc?: RTCPeerConnection } } }

let lastStatsLog = 0;
let zeroStatsCount = 0;

export function instrumentRoom(room: Room, label: string): void {
  DebugLog.info('ROOM_CREATED', { label });

  room.on(RoomEvent.Connected, () => DebugLog.in('ROOM_CONNECTED', { room: room.name, sid: room.localParticipant?.sid }));
  room.on(RoomEvent.Disconnected, (reason) => DebugLog.in('ROOM_DISCONNECTED', { reason: String(reason ?? 'unknown') }));
  room.on(RoomEvent.Reconnecting, () => DebugLog.warn('ROOM_RECONNECTING'));
  room.on(RoomEvent.Reconnected, () => DebugLog.in('ROOM_RECONNECTED'));
  room.on(RoomEvent.ConnectionStateChanged, (s) => DebugLog.info('CONNECTION_STATE', { state: String(s) }));
  room.on(RoomEvent.ConnectionQualityChanged, (q, p) => DebugLog.info('QUALITY', { who: p?.identity ?? 'self', quality: String(q) }));

  room.on(RoomEvent.ParticipantConnected,    (p: RemoteParticipant) => DebugLog.in('PARTICIPANT_CONNECTED',    { who: p.identity, sid: p.sid }));
  room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => DebugLog.in('PARTICIPANT_DISCONNECTED', { who: p.identity }));

  room.on(RoomEvent.LocalTrackPublished,   (pub: LocalTrackPublication) => DebugLog.out('TRACK_PUBLISHED_LOCAL',  { kind: pub.kind, source: pub.source, mid: pub.trackSid }));
  room.on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => DebugLog.out('TRACK_UNPUBLISHED_LOCAL', { kind: pub.kind }));
  room.on(RoomEvent.TrackPublished,   (pub, p) => DebugLog.in('TRACK_PUBLISHED_REMOTE', { from: p.identity, kind: pub.kind }));
  room.on(RoomEvent.TrackSubscribed,  (track, _pub, p) => DebugLog.in('TRACK_SUBSCRIBED',   { from: p.identity, kind: track.kind }));
  room.on(RoomEvent.TrackUnsubscribed,(track, _pub, p) => DebugLog.in('TRACK_UNSUBSCRIBED', { from: p.identity, kind: track.kind }));
  room.on(RoomEvent.TrackMuted,   (pub, p) => DebugLog.in('TRACK_MUTED',   { who: p.identity, kind: pub.kind }));
  room.on(RoomEvent.TrackUnmuted, (pub, p) => DebugLog.in('TRACK_UNMUTED', { who: p.identity, kind: pub.kind }));

  room.on(RoomEvent.DataReceived, (payload, p) => {
    let parsed: unknown = '(non-JSON)';
    try { parsed = JSON.parse(new TextDecoder().decode(payload)); } catch {}
    DebugLog.in('DATA_RECEIVED', { from: p?.identity ?? '?', bytes: payload.length, type: (parsed as { type?: string })?.type ?? '?' });
  });

  room.on(RoomEvent.MediaDevicesError, (err: Error) => DebugLog.warn('MEDIA_DEVICES_ERROR', { msg: err?.message ?? String(err) }));

  let timer: ReturnType<typeof setInterval> | null = null;
  room.on(RoomEvent.Connected, () => {
    if (timer) clearInterval(timer);
    zeroStatsCount = 0;
    timer = setInterval(async () => {
      try {
        const sub = (room as unknown as Mgr).pcManager?.subscriber;
        const pc = sub?.getConnectedPC?.() ?? sub?.pc;
        if (!pc) return;
        const stats = await pc.getStats();
        let inV = 0, frames = 0, inA = 0;
        stats.forEach((r) => {
          if (r.type === 'inbound-rtp' && (r as RTCStats & { kind?: string }).kind === 'video') {
            const i = r as RTCStats & { bytesReceived?: number; framesDecoded?: number };
            inV += i.bytesReceived ?? 0;
            frames = Math.max(frames, i.framesDecoded ?? 0);
          }
          if (r.type === 'inbound-rtp' && (r as RTCStats & { kind?: string }).kind === 'audio') {
            inA += (r as RTCStats & { bytesReceived?: number }).bytesReceived ?? 0;
          }
        });
        if (Date.now() - lastStatsLog > 5000) {
          const zero = inV === 0 && frames === 0 && inA === 0;
          if (zero) { zeroStatsCount += 1; if (zeroStatsCount > 3) return; } else { zeroStatsCount = 0; }
          lastStatsLog = Date.now();
          DebugLog.info('STATS', { videoBytes: inV, videoFrames: frames, audioBytes: inA });
        }
      } catch {}
    }, 2000);
  });
  room.on(RoomEvent.Disconnected, () => { if (timer) { clearInterval(timer); timer = null; } });
}
